import type { CookieApp } from '../types/cookies';
import { computed, watch } from 'vue';
import { useScriptGoogleTagManager } from '../../../.nuxt/imports';
import { useCookieAppConfig } from './useCookieAppConfig';
import { useCookieConsent } from './useCookieConsent';

export function useCookieScripts(appId: CookieApp) {
	const { consent } = useCookieConsent(appId);
	const { scripts } = useCookieAppConfig(appId);

	const consentStatus = computed(() => (consent.value.analytics ? 'granted' : 'denied'));

	const gtmBuildConsentPayload = (status: 'granted' | 'denied') => ({
		ad_storage: status,
		ad_user_data: status,
		ad_personalization: status,
		analytics_storage: status,
		functionality_storage: 'granted',
		personalization_storage: status,
		security_storage: 'granted',
	});

	const gtmUpdateConsent = (status: 'granted' | 'denied') => {
		if (!import.meta.client) {
			return;
		}

		const payload = gtmBuildConsentPayload(status);
		const gtag = (window as typeof window & { gtag?: (...args: unknown[]) => void }).gtag;
		if (typeof gtag === 'function') {
			gtag('consent', 'update', payload);
		}

		const dataLayer = (window as typeof window & { dataLayer?: unknown[] }).dataLayer;
		if (Array.isArray(dataLayer)) {
			dataLayer.push({ event: 'cookie_consent_update', ...payload });
		}
	};

	scripts.value.analytics?.forEach((script) => {
		if (script.type === 'google-tag-manager') {
			useScriptGoogleTagManager({
				id: script.id,
				onBeforeGtmStart: (gtmContext: unknown) => {
					const status = consentStatus.value;
					const payload = gtmBuildConsentPayload(status);
					const ctx = gtmContext as {
						gtag?: (...args: unknown[]) => void;
						consent?: { default?: (p: Record<string, unknown>) => void };
						push?: (p: Record<string, unknown>) => void;
					};

					if (typeof ctx?.gtag === 'function') {
						ctx.gtag('consent', 'default', payload);
					}
					else if (typeof ctx?.consent?.default === 'function') {
						ctx.consent.default(payload);
					}
					else if (typeof ctx?.push === 'function') {
						ctx.push({ event: 'gtm.consentInitialization', ...payload });
					}
				},
			});
		}
	});

	watch(
		consentStatus,
		(status) => {
			gtmUpdateConsent(status);
		},
		{ immediate: true },
	);
}
